import React, { useState } from 'react';
import { X, Mail, AlertCircle, CheckCircle } from 'lucide-react';

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void>;
  loading?: boolean;
}

const EmailCaptureModal: React.FC<EmailCaptureModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Por favor, introduce tu correo electrónico');
      return;
    }

    if (!validateEmail(email)) {
      setError('Por favor, introduce un correo electrónico válido');
      return;
    }

    setIsValidating(true);
    try {
      await onSubmit(email.trim().toLowerCase());
    } catch (err: any) {
      setError(err.message || 'Error al guardar. Por favor, intenta de nuevo.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl transform transition-all animate-slideUp">
        <button
          onClick={onClose}
          disabled={loading || isValidating}
          className="absolute top-4 right-4 p-2 text-stone-400 hover:text-stone-600 transition-colors rounded-full hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <X size={24} />
        </button>

        <div className="p-8">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-rose-100 rounded-full">
            <Mail className="w-8 h-8 text-rose-500" />
          </div>

          <h2 className="text-2xl font-serif font-bold text-center text-stone-900 mb-3">
            ¡Ya casi está!
          </h2>

          <p className="text-center text-stone-600 mb-6 leading-relaxed">
            Para enviarte el resultado y otra información útil sobre cómo preservar estos momentos, necesitamos tu correo electrónico.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                Correo electrónico
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="tu@email.com"
                disabled={loading || isValidating}
                className={`w-full px-4 py-3 text-base rounded-xl border-2 transition-colors focus:outline-none ${
                  error
                    ? 'border-red-300 focus:border-red-500'
                    : 'border-stone-300 focus:border-rose-500'
                } disabled:opacity-50 disabled:cursor-not-allowed bg-white`}
                autoComplete="email"
                autoFocus
              />

              {error && (
                <div className="flex items-start gap-2 mt-2 text-sm text-red-600">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || isValidating || !email.trim()}
              className="w-full py-4 px-6 bg-rose-500 text-white font-bold text-lg rounded-xl hover:bg-rose-600 transition-all shadow-lg hover:shadow-rose-500/30 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none flex items-center justify-center gap-2"
            >
              {loading || isValidating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  <span>Ver Mi Resultado</span>
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-center text-stone-500 mt-4">
            No compartiremos tu correo con terceros. Usaremos esta información únicamente para enviarte contenido relacionado con la preservación de memorias familiares.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailCaptureModal;
